import { useCallback, useRef } from 'react';
import classNames from 'classnames';

import { Country } from '@xemplo/country-helper';
import { V2, V2Types } from '@xemplo/popover';
import { List } from '@xemplo/stateful-list';
import { ListValue } from '@xemplo/stateful-list-core';

import { FlagsSprite } from '../flags/flags-sprite';
import { usePhoneFieldContext } from '../phone-field.context';

import { TriggerElement } from './trigger-element';

export function CountrySelector({ parentId }: { parentId?: string }) {
  const popoverRef = useRef<V2Types.PopoverRef>(null);
  const {
    useSprite,
    metadata: { flagProps, inputSize, disabled, readOnly, onValueChange },
    state: { items, selectedCountry, setSelectedCountry },
    inputProps: { value },
  } = usePhoneFieldContext();
  const { onCountryChange } = flagProps;

  const handleListChange = useCallback(
    (newCountryCode?: ListValue) => {
      const castedCountryCode = newCountryCode as Country.Code;
      setSelectedCountry(castedCountryCode);
      onCountryChange?.(castedCountryCode);
      popoverRef.current?.setOpen(false);

      if (!onValueChange) return;
      const countryCode = Country.getPhoneCodeByCountryCode(castedCountryCode);
      const combinedValue = `${countryCode} ${value}`;
      onValueChange(combinedValue);
    },
    [onCountryChange, setSelectedCountry, onValueChange, value],
  );

  return (
    <V2.Popover.Root ref={popoverRef} disabled={disabled || readOnly}>
      {useSprite && <FlagsSprite />}
      <V2.Popover.Trigger>
        {({ open }) => (
          <TriggerElement
            className={classNames(inputSize, { open, disabled, readOnly })}
          />
        )}
      </V2.Popover.Trigger>
      <V2.Popover.Popup>
        <List.SingleSelect
          items={items}
          onChange={handleListChange}
          triggerId={parentId}
          defaultSelected={selectedCountry}
          useSearch
        />
      </V2.Popover.Popup>
    </V2.Popover.Root>
  );
}
